import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { OverlayPanel } from "primereact/overlaypanel";
import { Dropdown } from "primereact/dropdown";
import { DynamicField } from "../../components/form/DynamicField";
import { FieldType } from "../../api/types/field-type";
import { ProfileFieldValue } from "../../api/types/crm/profile-fields";
import { useBulkUpsertFieldValues } from "../../api/mutations/field-values/useBulkFieldValues";
import { useTagList } from "../../api/queries/tag/list";
import { NestedGroupRenderer } from "./NestedGroupRenderer";
import {
  hasValidTagData,
  buildTagToFieldValuesMap,
} from "../../utils/tagUtils";

export interface GroupInstance {
  sortOrder: number;
  groupFieldValueId: string | null; // Track the group field value ID for this instance
  fields: Record<string, any>; // fieldTypeId => value
}

export interface CollectedField {
  id?: string;
  field_type_id: string;
  value: any;
  sort_order: number;
  parent_id?: string | null;
}

interface GroupFieldSectionProps {
  groupFieldType: FieldType;
  childFieldTypes: FieldType[];
  profileId: string;
  existingFieldValues?: ProfileFieldValue[];
  parentFieldValueId?: string | null; // ID of parent field value (for nested groups)
  onSaveSuccess?: () => void;
  onSaveError?: (error: string) => void;
  onRegisterCollector?: (
    groupFieldTypeId: string,
    collector: (enforceSortOrder?: number | null) => CollectedField[],
  ) => void;
  onRegisterSaver?: (
    groupFieldTypeId: string,
    saver: () => Promise<void>,
  ) => void;
  onUnsavedChange?: (groupId: string, hasUnsaved: boolean) => void;
}

const GroupFieldSectionComponent: React.FC<GroupFieldSectionProps> = ({
  groupFieldType,
  childFieldTypes,
  profileId,
  existingFieldValues = [],
  parentFieldValueId = null,
  onSaveSuccess,
  onSaveError,
  onRegisterCollector,
  onRegisterSaver,
  onUnsavedChange,
}) => {
  const bulkUpsertMutation = useBulkUpsertFieldValues();
  const { data: tagListResponse } = useTagList();
  const overlayPanelRef = useRef<OverlayPanel>(null);
  const [editingTag, setEditingTag] = useState<{
    tagFieldValueIds: string[];
    currentTagId: string;
    parentFieldValueId: string;
    anchorElement: HTMLElement | null;
  } | null>(null);

  // Collectors for nested groups - each collector returns fully collected fields
  const nestedCollectorsRef = useRef<
    Map<string, (enforceSortOrder?: number | null) => CollectedField[]>
  >(new Map());

  // Tag options for dropdown
  const tagOptions = useMemo(
    () =>
      tagListResponse?.data?.results?.map((tag) => ({
        label: tag.name,
        value: tag.id,
      })) ?? [],
    [tagListResponse?.data?.results],
  );

  // Filter field values by parent
  const filteredFieldValues = useMemo(() => {
    // Get direct child field type IDs for filtering
    const directChildFieldTypeIds = childFieldTypes.map((ft) => ft.id);

    // Case 1: Root level (parentFieldValueId === null)
    // Filter by parentId=null AND (child field OR group field itself)
    if (parentFieldValueId === null) {
      const filtered = existingFieldValues.filter(
        (fv) =>
          (fv.parentId || null) === null &&
          (directChildFieldTypeIds.includes(fv.fieldTypeId) ||
            (fv.fieldTypeId === groupFieldType.id && fv.type === "group")),
      );

      return filtered;
    }

    // Case 2: Temp ID (new unsaved instance). No saved data will have temp ID as parent
    if (parentFieldValueId.startsWith("temp-")) {
      return [];
    }

    // Case 3: Real parent ID (existing instance). Match by parentId
    const byParentId = existingFieldValues.filter(
      (fv) => fv.parentId === parentFieldValueId,
    );

    return byParentId;
  }, [
    existingFieldValues,
    parentFieldValueId,
    childFieldTypes,
    groupFieldType.id,
  ]);

  // Create a stable key for filteredFieldValues to detect real changes
  const fieldValuesKey = useMemo(() => {
    return JSON.stringify(
      filteredFieldValues.map((fv) => ({
        id: fv.id,
        fieldTypeId: fv.fieldTypeId,
        value: fv.value,
        sortOrder: fv.sortOrder,
        parentId: fv.parentId,
      })),
    );
  }, [filteredFieldValues]);

  // Detect legacy data (all parentId values are null and we're at root level with nested children)
  const isLegacyData = useMemo(() => {
    // Only applicable at root level (parentFieldValueId === null)
    if (parentFieldValueId !== null) return false;

    // Check if all filtered field values have null parentId
    const hasNullParents =
      filteredFieldValues.length > 0 &&
      filteredFieldValues.every((fv) => fv.parentId === null);

    // Check if any child field types should have parent (nested structure exists)
    const hasNestedStructure = childFieldTypes.some(
      (ft) => ft.parent !== undefined && ft.parent !== null,
    );

    return hasNullParents && hasNestedStructure;
  }, [filteredFieldValues, parentFieldValueId, childFieldTypes]);

  // Group existing field values by sortOrder
  const initialInstances = useMemo(() => {
    const grouped = new Map<number, GroupInstance>();

    // First, find the group-type field values (one per instance) to establish the parent for direct children
    const groupFieldValueIds = new Map<number, string>();
    filteredFieldValues.forEach((fv) => {
      if (fv.fieldTypeId === groupFieldType.id && fv.type === "group") {
        groupFieldValueIds.set(fv.sortOrder || 0, fv.id);
      }
    });

    // Ensure we have an instance placeholder for every discovered group value
    groupFieldValueIds.forEach((groupValueId, sortOrder) => {
      if (!grouped.has(sortOrder)) {
        grouped.set(sortOrder, {
          sortOrder,
          groupFieldValueId: groupValueId,
          fields: {},
        });
      }
    });

    // Helper to process and assign a value into an instance
    const assignProcessedValue = (
      instance: GroupInstance,
      fieldValue: ProfileFieldValue,
    ) => {
      let processedValue = fieldValue.value as any;
      const fieldType = childFieldTypes.find(
        (ft) => ft.id === fieldValue.fieldTypeId,
      );

      if (
        fieldValue.type === "reference" &&
        typeof fieldValue.value === "string"
      ) {
        try {
          processedValue = JSON.parse(fieldValue.value);
        } catch (e) {
          console.error(
            "[GroupFieldSection] Failed to parse reference field JSON:",
            fieldValue.value,
            e,
          );
        }
      } else if (
        fieldValue.type === "list" &&
        typeof fieldValue.value === "string"
      ) {
        try {
          processedValue = JSON.parse(fieldValue.value);
        } catch (e) {
          console.error(
            "[GroupFieldSection] Failed to parse list field JSON:",
            fieldValue.value,
            e,
          );
        }
      }

      // Handle multi-select reference fields (already arrays from backend)
      if (fieldValue.type === "reference" && Array.isArray(processedValue)) {
        // Keep array for multi-select, extract first for single-select
        if (!fieldType?.allowMultiple && processedValue.length > 0) {
          processedValue = processedValue[0];
        }
      }

      if (
        fieldValue.type === "list" &&
        Array.isArray(processedValue) &&
        processedValue.length > 0 &&
        !fieldType?.allowMultiple
      ) {
        processedValue = processedValue[0];
      }

      instance.fields[fieldValue.fieldTypeId] = processedValue;
    };

    // Populate direct child fields for each instance by matching parentId to the group's field value ID
    const regularFieldTypeIds = new Set(
      childFieldTypes.filter((ft) => ft.type !== "group").map((ft) => ft.id),
    );
    grouped.forEach((instance) => {
      // First try to find children by parentId
      let directChildren = existingFieldValues.filter(
        (fv) =>
          fv.parentId === instance.groupFieldValueId &&
          fv.type !== "group" &&
          regularFieldTypeIds.has(fv.fieldTypeId),
      );

      // Fallback: if no children found by parentId (e.g., Tally data has parentId=null),
      // try to match by sortOrder instead
      if (directChildren.length === 0) {
        directChildren = existingFieldValues.filter(
          (fv) =>
            fv.parentId === null &&
            fv.sortOrder === instance.sortOrder &&
            fv.type !== "group" &&
            regularFieldTypeIds.has(fv.fieldTypeId),
        );
      }

      directChildren.forEach((fv) => assignProcessedValue(instance, fv));
    });

    // LEGACY DATA HANDLING: all parentId are null (older data). Fall back to grouping by sortOrder from filtered values
    if (grouped.size === 0 && isLegacyData) {
      console.warn(
        "[GroupFieldSection] Legacy data detected - parent relationships missing. Grouping may be inaccurate.",
      );
      const directChildFieldTypeIds = new Set(
        childFieldTypes.map((ft) => ft.id),
      );
      filteredFieldValues
        .filter(
          (fv) =>
            fv.type !== "group" && directChildFieldTypeIds.has(fv.fieldTypeId),
        )
        .forEach((fv) => {
          const sortOrder = fv.sortOrder || 0;
          if (!grouped.has(sortOrder)) {
            grouped.set(sortOrder, {
              sortOrder,
              groupFieldValueId: null,
              fields: {},
            });
          }
          assignProcessedValue(grouped.get(sortOrder)!, fv);
        });
    }

    const instances = Array.from(grouped.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );

    if (instances.length === 0) {
      instances.push({ sortOrder: 0, groupFieldValueId: null, fields: {} });
    }

    return instances;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValuesKey, groupFieldType.id, isLegacyData]); // Only recompute when actual field values change

  const [instances, setInstances] = useState<GroupInstance[]>(initialInstances);
  const instancesRef = useRef<GroupInstance[]>(initialInstances);
  const [isInitialized, setIsInitialized] = useState(false);
  const [unsavedFields, setUnsavedFields] = useState<Set<string>>(new Set());
  const nestedUnsavedRef = useRef<Map<string, boolean>>(new Map());
  const [hasNestedUnsaved, setHasNestedUnsaved] = useState(false);
  // Track which sortOrders have been saved to backend
  const [savedSortOrders, setSavedSortOrders] = useState<Set<number>>(
    () => new Set(filteredFieldValues.map((fv) => fv.sortOrder)),
  );

  // Track last processed data to avoid unnecessary re-initialization
  const lastProcessedId = useRef<string>("");

  // Keep a ref to the latest instances for stable collector
  useEffect(() => {
    instancesRef.current = instances;
  }, [instances]);

  // Recursively collect fields from this level AND all nested groups
  const collectAllFieldsRecursively = useCallback(
    (
      currentInstances: GroupInstance[],
      currentChildFieldTypes: FieldType[],
      currentParentFieldValueId: string | null,
      currentGroupFieldType?: FieldType,
      enforceSortOrder?: number | null,
    ): CollectedField[] => {
      const fieldsToUpdate: CollectedField[] = [];

      // Separate regular fields from nested groups
      const regularFieldsAtThisLevel = currentChildFieldTypes.filter(
        (ft) => ft.type !== "group",
      );
      const childGroupsAtThisLevel = currentChildFieldTypes.filter(
        (ft) => ft.type === "group",
      );

      // Collect fields from current level instances
      currentInstances.forEach((instance) => {
        const effectiveSortOrder = enforceSortOrder ?? instance.sortOrder;
        const parentForGroupCandidate =
          currentParentFieldValueId &&
          !currentParentFieldValueId.startsWith("temp-") &&
          existingFieldValues.some((fv) => fv.id === currentParentFieldValueId)
            ? currentParentFieldValueId
            : null;
        const directGroupId =
          instance.groupFieldValueId &&
          !instance.groupFieldValueId.startsWith("temp-") &&
          existingFieldValues.some((fv) => fv.id === instance.groupFieldValueId)
            ? instance.groupFieldValueId
            : undefined;
        const resolvedGroupValue = currentGroupFieldType
          ? directGroupId
            ? existingFieldValues.find((fv) => fv.id === directGroupId)
            : existingFieldValues.find(
                (fv) =>
                  fv.fieldTypeId === currentGroupFieldType.id &&
                  fv.sortOrder === instance.sortOrder &&
                  (fv.parentId || null) === parentForGroupCandidate,
              )
          : undefined;
        const resolvedGroupId = resolvedGroupValue?.id ?? directGroupId;
        // First, add the group field value itself (so backend creates parent before children)
        if (currentGroupFieldType) {
          // Include existing ID if this is a saved group (not a temp ID)
          const groupId =
            instance.groupFieldValueId &&
            !instance.groupFieldValueId.startsWith("temp-")
              ? instance.groupFieldValueId
              : undefined;
          const parentForGroup = parentForGroupCandidate;
          let groupValue = resolvedGroupValue?.value;
          if (
            groupValue === null ||
            groupValue === undefined ||
            groupValue === ""
          ) {
            groupValue = `${currentGroupFieldType.name} #${effectiveSortOrder + 1}`;
          }
          if (typeof groupValue !== "string") {
            groupValue = String(groupValue);
          }

          fieldsToUpdate.push({
            id: resolvedGroupId ?? groupId,
            field_type_id: currentGroupFieldType.id,
            value: groupValue,
            sort_order: effectiveSortOrder,
            parent_id: parentForGroup,
          });
        }

        // Add regular fields at this level
        regularFieldsAtThisLevel.forEach((fieldType) => {
          const value = instance.fields[fieldType.id];
          let valueToSave;

          if (value === undefined || value === null || value === "") {
            // For list fields, send empty array so backend deletes existing value
            valueToSave = fieldType.type === "list" ? [] : null;
          } else if (
            fieldType.type === "list" &&
            Array.isArray(value) &&
            value.length === 0
          ) {
            valueToSave = [];
          } else if (
            fieldType.type === "reference" &&
            typeof value === "object" &&
            value !== null
          ) {
            // Handle multi-select reference (array of {id, label})
            if (Array.isArray(value)) {
              valueToSave = value.map((v) => v?.id || v).filter(Boolean);
            } else if (value.id) {
              // Single reference
              valueToSave = value.id;
            } else {
              valueToSave = value;
            }
          } else {
            valueToSave = value;
          }

          // Prefer explicit parent when we have a stable group FieldValue ID
          const parentForThisLevel = resolvedGroupId ?? null;

          // Find existing FieldValue ID by matching fieldTypeId and parentId
          const existingFv = existingFieldValues.find(
            (fv) =>
              fv.fieldTypeId === fieldType.id &&
              fv.parentId === instance.groupFieldValueId,
          );

          fieldsToUpdate.push({
            id: existingFv?.id,
            field_type_id: fieldType.id,
            value: valueToSave,
            sort_order: effectiveSortOrder,
            parent_id: parentForThisLevel,
          });
        });

        // Collect from nested groups - each nested collector returns its fully collected fields
        if (childGroupsAtThisLevel.length > 0) {
          childGroupsAtThisLevel.forEach((childGroup) => {
            // Get collected fields from nested component via collector callback
            const collectorKey = `${instance.groupFieldValueId || instance.sortOrder}-${childGroup.id}`;
            const nestedCollector =
              nestedCollectorsRef.current.get(collectorKey);
            if (nestedCollector) {
              const nestedFields = nestedCollector(effectiveSortOrder);
              fieldsToUpdate.push(...nestedFields);
            }
          });
        }
      });

      return fieldsToUpdate;
    },
    [existingFieldValues],
  );

  // Create a collector function that returns fully collected fields for this component's subtree
  const collectFieldsForThisGroup = useCallback(
    (enforceSortOrder?: number | null): CollectedField[] =>
      collectAllFieldsRecursively(
        instancesRef.current,
        childFieldTypes,
        parentFieldValueId,
        groupFieldType,
        enforceSortOrder,
      ),
    [
      childFieldTypes,
      parentFieldValueId,
      groupFieldType,
      collectAllFieldsRecursively,
    ],
  );

  // Register this component's collector with parent
  useEffect(() => {
    if (onRegisterCollector) {
      onRegisterCollector(groupFieldType.id, collectFieldsForThisGroup);
    }
  }, [onRegisterCollector, groupFieldType.id, collectFieldsForThisGroup]);

  // Notify parent when unsaved state changes (local or nested)
  useEffect(() => {
    if (onUnsavedChange) {
      const hasUnsaved = unsavedFields.size > 0 || hasNestedUnsaved;
      onUnsavedChange(groupFieldType.id, hasUnsaved);
    }
  }, [
    onUnsavedChange,
    groupFieldType.id,
    unsavedFields.size,
    hasNestedUnsaved,
  ]);

  // Handler for nested groups to report their unsaved state
  const handleNestedUnsavedChange = useCallback(
    (nestedGroupId: string, hasUnsaved: boolean) => {
      nestedUnsavedRef.current.set(nestedGroupId, hasUnsaved);
      const anyNestedUnsaved = Array.from(
        nestedUnsavedRef.current.values(),
      ).some(Boolean);
      setHasNestedUnsaved(anyNestedUnsaved);
    },
    [],
  );

  // Sync instances when profile changes OR when initial data loads
  useEffect(() => {
    const dataKey = `${profileId}-${parentFieldValueId}-${fieldValuesKey}`;

    // Reset initialization flag when profile or data changes
    if (lastProcessedId.current !== dataKey) {
      setInstances(initialInstances);
      setSavedSortOrders(
        new Set(filteredFieldValues.map((fv) => fv.sortOrder)),
      );
      setIsInitialized(true);
      setUnsavedFields(new Set());
      nestedUnsavedRef.current.clear();
      setHasNestedUnsaved(false);
      lastProcessedId.current = dataKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, parentFieldValueId, fieldValuesKey]); // Re-sync when profile OR parent OR data changes

  const addInstance = () => {
    const newSortOrder =
      instances.length > 0
        ? Math.max(...instances.map((i) => i.sortOrder)) + 1
        : 0;

    // Generate temporary ID for new unsaved instances
    // This allows nested groups to render immediately without data collision
    const tempId = `temp-${Date.now()}-${newSortOrder}`;

    setInstances([
      ...instances,
      {
        sortOrder: newSortOrder,
        groupFieldValueId: tempId,
        fields: {},
      },
    ]);
  };

  const removeInstance = async (sortOrder: number) => {
    // Remove from UI immediately with updated sortOrders
    const updatedInstances = instances
      .filter((instance) => instance.sortOrder !== sortOrder)
      .map((instance) =>
        instance.sortOrder > sortOrder
          ? { ...instance, sortOrder: instance.sortOrder - 1 }
          : instance,
      );

    setInstances(updatedInstances);

    // Only send delete request if this instance has been saved to backend
    if (!savedSortOrders.has(sortOrder)) {
      return;
    }

    try {
      // Reuse recursive collector to send full, authoritative state
      const fieldsToUpdate = collectAllFieldsRecursively(
        updatedInstances,
        childFieldTypes,
        parentFieldValueId,
        groupFieldType,
      );

      await bulkUpsertMutation.mutateAsync({
        profile_id: profileId,
        fields: fieldsToUpdate,
        replace_mode: true,
      });

      // Update savedSortOrders
      setSavedSortOrders(new Set(updatedInstances.map((i) => i.sortOrder)));

      onSaveSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to remove instance";
      console.error("[GroupFieldSection] Failed to remove instance:", error);
      onSaveError?.(errorMessage);

      // On error, revert to original instances
      setInstances(instances);
    }
  };

  // Manual save function - saves ALL fields from ALL instances (including nested groups)
  const handleSave = useCallback(async () => {
    try {
      // Use recursive collection to get ALL fields including nested groups
      const fieldsToUpdate = collectAllFieldsRecursively(
        instances,
        childFieldTypes,
        parentFieldValueId,
        groupFieldType, // Pass current group FieldType so it gets added to payload
      );

      await bulkUpsertMutation.mutateAsync({
        profile_id: profileId,
        fields: fieldsToUpdate,
        replace_mode: true,
      });

      // Mark all instances as saved
      setSavedSortOrders(new Set(instances.map((i) => i.sortOrder)));
      setUnsavedFields(new Set());
      nestedUnsavedRef.current.clear();
      setHasNestedUnsaved(false);
      onSaveSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save fields";
      console.error("[GroupFieldSection] Save failed:", {
        error,
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      onSaveError?.(errorMessage);
    }
  }, [
    bulkUpsertMutation,
    childFieldTypes,
    groupFieldType,
    instances,
    onSaveError,
    onSaveSuccess,
    parentFieldValueId,
    profileId,
    collectAllFieldsRecursively,
  ]);

  // Register this component's save function with parent (root sections only)
  useEffect(() => {
    if (onRegisterSaver && parentFieldValueId === null) {
      onRegisterSaver(groupFieldType.id, handleSave);
    }
  }, [onRegisterSaver, groupFieldType.id, handleSave, parentFieldValueId]);

  // Tag chip click handler - opens overlay for editing
  const handleTagClick = (
    tagFieldValues: ProfileFieldValue[],
    tagId: string,
    parentFieldValueId: string,
    event: React.MouseEvent<HTMLSpanElement>,
  ) => {
    setEditingTag({
      tagFieldValueIds: tagFieldValues.map((fv) => fv.id),
      currentTagId: tagId,
      parentFieldValueId: parentFieldValueId,
      anchorElement: event.currentTarget,
    });
    overlayPanelRef.current?.show(event, event.currentTarget);
  };

  // Replace a tag with a different one in all field values that contain it
  const handleReplaceTag = async (newTagId: string) => {
    if (!editingTag) return;

    // Find all tag field values that need updating
    const tagFieldValues = existingFieldValues.filter((fv) =>
      editingTag.tagFieldValueIds.includes(fv.id),
    );

    if (tagFieldValues.length === 0) {
      overlayPanelRef.current?.hide();
      setEditingTag(null);
      return;
    }

    const newTag = tagListResponse?.data?.results?.find(
      (t) => t.id === newTagId,
    );

    if (!newTag) return;

    // Update all field values that contain this tag
    const fieldsToUpdate = tagFieldValues.map((tagFieldValue) => {
      let currentTags = tagFieldValue.value as
        | Array<{ id: string; label: string }>
        | string
        | null
        | undefined;

      if (typeof currentTags === "string") {
        try {
          currentTags = JSON.parse(currentTags);
        } catch (error) {
          currentTags = [];
        }
      }

      if (!Array.isArray(currentTags)) {
        currentTags = [];
      }

      const updatedTags = currentTags.map((tag) =>
        tag.id === editingTag.currentTagId
          ? { id: newTag.id, label: newTag.name }
          : tag,
      );

      // Deduplicate tags to ensure unique IDs
      const uniqueTags = updatedTags.filter(
        (tag, index, self) => index === self.findIndex((t) => t.id === tag.id),
      );

      return {
        field_type_id: tagFieldValue.fieldTypeId,
        value: uniqueTags.map((t) => t.id),
        parent_id: editingTag.parentFieldValueId,
        sort_order: tagFieldValue.sortOrder,
      };
    });

    // Deduplicate fields to update (ensure we don't send multiple updates for same field+parent+sort)
    const uniqueFieldsToUpdate = fieldsToUpdate.filter(
      (field, index, self) =>
        index ===
        self.findIndex(
          (f) =>
            f.field_type_id === field.field_type_id &&
            f.parent_id === field.parent_id &&
            f.sort_order === field.sort_order,
        ),
    );

    // Update via bulk upsert
    try {
      await bulkUpsertMutation.mutateAsync({
        profile_id: profileId,
        fields: uniqueFieldsToUpdate,
        // Replace to avoid duplicate rows for the same parent/sort
        replace_mode: true,
      });

      if (onSaveSuccess) onSaveSuccess();
      overlayPanelRef.current?.hide();
      setEditingTag(null);
    } catch (error: any) {
      if (onSaveError) onSaveError(error.message);
    }
  };

  // Remove a tag from all field values that contain it
  const handleRemoveTag = async () => {
    if (!editingTag) return;

    const tagFieldValues = existingFieldValues.filter((fv) =>
      editingTag.tagFieldValueIds.includes(fv.id),
    );

    if (tagFieldValues.length === 0) {
      overlayPanelRef.current?.hide();
      setEditingTag(null);
      return;
    }

    // Update all field values that contain this tag
    const fieldsToUpdate = tagFieldValues.map((tagFieldValue) => {
      let currentTags = tagFieldValue.value as
        | Array<{ id: string; label: string }>
        | string
        | null
        | undefined;

      if (typeof currentTags === "string") {
        try {
          currentTags = JSON.parse(currentTags);
        } catch (error) {
          currentTags = [];
        }
      }

      if (!Array.isArray(currentTags)) {
        currentTags = [];
      }

      const updatedTags = currentTags.filter(
        (tag) => tag.id !== editingTag.currentTagId,
      );

      return {
        field_type_id: tagFieldValue.fieldTypeId,
        value: updatedTags.map((t) => t.id),
        parent_id: editingTag.parentFieldValueId,
        sort_order: tagFieldValue.sortOrder,
      };
    });

    try {
      await bulkUpsertMutation.mutateAsync({
        profile_id: profileId,
        fields: fieldsToUpdate,
        // Replace to avoid duplicate rows for the same parent/sort
        replace_mode: true,
      });

      if (onSaveSuccess) onSaveSuccess();
      overlayPanelRef.current?.hide();
      setEditingTag(null);
    } catch (error: any) {
      if (onSaveError) onSaveError(error.message);
    }
  };

  const updateField = (sortOrder: number, fieldTypeId: string, value: any) => {
    // Find the field type to determine if this is a reference field with search
    const fieldType = childFieldTypes.find((ft) => ft.id === fieldTypeId);

    // Normalize value for UI storage (keep full object for reference fields to display label)
    const normalizedValueForUI = value ?? "";

    // Update UI immediately (optimistic update)
    setInstances((prev) =>
      prev.map((instance) =>
        instance.sortOrder === sortOrder
          ? {
              ...instance,
              fields: {
                ...instance.fields,
                [fieldTypeId]: normalizedValueForUI,
              },
            }
          : instance,
      ),
    );

    // Determine if we should trigger auto-save
    // For reference fields: Only save when user has SELECTED a value (object with id or array)
    // For other fields: Save on any change
    const shouldTriggerSave =
      fieldType?.type === "reference"
        ? typeof value === "object" &&
          value !== null &&
          (value.id || (Array.isArray(value) && value.length > 0)) // Single or multi-select
        : true; // Always save for non-reference fields

    if (shouldTriggerSave) {
      // Mark this specific field as unsaved
      const fieldKey = `${sortOrder}-${fieldTypeId}`;
      setUnsavedFields((prev) => {
        const next = new Set(prev);
        next.add(fieldKey);
        return next;
      });
    }
  };

  // Separate child field types into regular fields vs nested groups
  const regularFields = childFieldTypes.filter((ft) => ft.type !== "group");
  const childGroups = childFieldTypes.filter((ft) => ft.type === "group");

  //

  return (
    <div className={`group-field-section ${!parentFieldValueId ? "mt-5" : ""}`}>
      {instances.map((instance, index) => (
        <Card
          key={`instance-${instance.sortOrder}-${fieldValuesKey.substring(0, 8)}`}
          className="mb-3"
          title={
            <div className="flex justify-content-between align-items-center">
              <span>
                {groupFieldType.name} #{index + 1}
                {instance.groupFieldValueId?.startsWith("temp-") && (
                  <span className="text-orange-500 ml-2 text-sm font-normal">
                    (Unsaved)
                  </span>
                )}
              </span>
              {instances.length > 1 && (
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="p-button-danger p-button-text p-button-sm"
                  onClick={() => removeInstance(instance.sortOrder)}
                  tooltip="Remove this entry"
                  tooltipOptions={{ position: "top" }}
                />
              )}
            </div>
          }
        >
          <div className="grid">
            {regularFields.map((fieldType: FieldType) => {
              const getFieldOptions = () => {
                if (fieldType.type === "list") {
                  if (fieldType.validationRules?.list_items) {
                    return fieldType.validationRules.list_items.map(
                      (item: string) => ({
                        label: item,
                        value: item,
                      }),
                    );
                  }
                  if (fieldType.validationRules?.options) {
                    return fieldType.validationRules.options.map(
                      (option: any) => ({
                        label: option.label || option,
                        value: option.value || option,
                      }),
                    );
                  }
                }
                return [];
              };

              const fieldKey = `${instance.sortOrder}-${fieldType.id}`;
              const fieldValue = instance.fields[fieldType.id];
              const options = getFieldOptions();

              // Create a DOM-safe ID by replacing hyphens with underscores in UUID
              const domSafeId = fieldKey.replace(/-/g, "_");

              // Find the field value ID for video/image fields that need it for deletion
              // Use filteredFieldValues (derived from current props) to find the group parent,
              // avoiding timing issues between state and props during re-renders
              const groupFvForSortOrder = filteredFieldValues.find(
                (fv) =>
                  fv.type === "group" && fv.sortOrder === instance.sortOrder,
              );

              // First, try to find a field value that has ANY children (prioritize the one with children)
              // This catches tag children regardless of how they're typed
              const existingFieldValue =
                existingFieldValues.find(
                  (fv) =>
                    fv.fieldTypeId === fieldType.id &&
                    fv.sortOrder === instance.sortOrder &&
                    existingFieldValues.some(
                      (child) => child.parentId === fv.id,
                    ),
                ) ||
                existingFieldValues.find(
                  // Fallback: match by parentId or sortOrder
                  (fv) =>
                    fv.fieldTypeId === fieldType.id &&
                    (fv.parentId === groupFvForSortOrder?.id ||
                      fv.sortOrder === instance.sortOrder),
                );

              // Find tag FieldValues that are children of this field value (reference type with valid tag data)
              const tagFieldValues = existingFieldValue
                ? existingFieldValues.filter(
                    (fv) =>
                      fv.parentId === existingFieldValue.id &&
                      fv.type === "reference" &&
                      hasValidTagData(fv),
                  )
                : [];

              // Build a map of tag ID to all field values that contain it (deduplicated)
              const tagsWithSource = Array.from(
                buildTagToFieldValuesMap(tagFieldValues).values(),
              );

              return (
                <div key={fieldKey} className="col-12 md:col-6">
                  <DynamicField
                    key={`${fieldKey}-${isInitialized ? "init" : "uninit"}`}
                    fieldId={domSafeId}
                    backendFieldId={fieldType.id}
                    fieldType={fieldType.type}
                    label={fieldType.name}
                    value={fieldValue}
                    onChange={(value) =>
                      updateField(instance.sortOrder, fieldType.id, value)
                    }
                    isRequired={fieldType.required}
                    isUnsaved={unsavedFields.has(fieldKey)}
                    placeholder={fieldType.defaultValue || undefined}
                    options={options}
                    referenceEntity={fieldType.referenceEntity}
                    allowMultiple={fieldType.allowMultiple}
                    validationRules={fieldType.validationRules}
                    fieldValueId={existingFieldValue?.id}
                  />
                  {/* Display tag chips if this field has tag children */}
                  {tagsWithSource.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tagsWithSource.map(({ tag, fieldValues }) => (
                        <span
                          key={tag.id}
                          className="px-2 py-1 border-round text-xs cursor-pointer"
                          style={{
                            backgroundColor: "#e0e7ff",
                            color: "#3730a3",
                            transition: "all 0.2s",
                          }}
                          onClick={(e) =>
                            handleTagClick(
                              fieldValues,
                              tag.id,
                              existingFieldValue!.id,
                              e,
                            )
                          }
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#c7d2fe";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#e0e7ff";
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleTagClick(
                                fieldValues,
                                tag.id,
                                existingFieldValue!.id,
                                e as any,
                              );
                            }
                          }}
                          aria-label={`Edit tag ${tag.label}`}
                        >
                          <i
                            className="pi pi-tag mr-1"
                            style={{ fontSize: "0.7rem" }}
                          />
                          {tag.label}
                          <i
                            className="pi pi-pencil ml-1"
                            style={{ fontSize: "0.6rem", opacity: 0.7 }}
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Render nested groups */}
          {childGroups.length > 0 && (
            <div className="mt-3">
              {childGroups.map((childGroup) => {
                return (
                  <NestedGroupRenderer
                    key={`${instance.groupFieldValueId || instance.sortOrder}-${childGroup.id}`}
                    groupFieldType={childGroup}
                    profileId={profileId}
                    existingFieldValues={existingFieldValues}
                    parentFieldValueId={instance.groupFieldValueId}
                    onSaveSuccess={onSaveSuccess}
                    onSaveError={onSaveError}
                    onRegisterCollector={(groupId, collector) => {
                      const collectorKey = `${instance.groupFieldValueId || instance.sortOrder}-${childGroup.id}`;
                      nestedCollectorsRef.current.set(collectorKey, collector);
                    }}
                    onUnsavedChange={handleNestedUnsavedChange}
                  />
                );
              })}
            </div>
          )}
        </Card>
      ))}

      {(() => {
        const hasUnsaved = unsavedFields.size > 0 || hasNestedUnsaved;
        const saveIcon = hasUnsaved ? "pi pi-save" : "pi pi-check";
        const saveLabel = hasUnsaved ? "Save" : "Saved";
        const saveTooltip = hasUnsaved ? "Save changes" : "All changes saved";
        return (
          <div
            className={`flex justify-content-between align-items-center gap-2 ${parentFieldValueId ? "mt-3" : "mt-3"}`}
          >
            <Button
              type="button"
              icon={saveIcon}
              label={saveLabel}
              className="p-button-success p-button-sm"
              onClick={handleSave}
              loading={bulkUpsertMutation.isLoading}
              disabled={!hasUnsaved}
              tooltip={saveTooltip}
              tooltipOptions={{ position: "top" }}
            />
            <Button
              type="button"
              icon="pi pi-plus"
              label={`Add ${groupFieldType.name}`}
              className="p-button-outlined p-button-sm"
              onClick={addInstance}
              tooltip={`Add another ${groupFieldType.name}`}
              tooltipOptions={{ position: "top" }}
            />
          </div>
        );
      })()}

      {/* Root-level separator below controls to keep them with this section */}
      {!parentFieldValueId && (
        <div
          style={{ height: 1, backgroundColor: "#000", opacity: 0.15 }}
          className="mt-3 mb-4"
        />
      )}

      {/* Overlay panel for editing tags */}
      <OverlayPanel
        ref={overlayPanelRef}
        onHide={() => setEditingTag(null)}
        dismissable
        showCloseIcon
        style={{ width: "300px" }}
      >
        <div className="flex flex-column gap-2">
          <label className="font-semibold text-sm">Replace Tag</label>
          <Dropdown
            value={editingTag?.currentTagId}
            options={tagOptions.filter(
              (opt) => opt.value !== editingTag?.currentTagId,
            )}
            onChange={(e) => handleReplaceTag(e.value)}
            placeholder="Select a tag"
            filter
            autoFocus
            className="w-full"
            disabled={bulkUpsertMutation.isLoading}
          />
          <div className="flex gap-2 mt-2">
            <Button
              label="Remove Tag"
              icon="pi pi-trash"
              severity="danger"
              size="small"
              outlined
              onClick={handleRemoveTag}
              disabled={bulkUpsertMutation.isLoading}
              className="flex-1"
            />
            <Button
              label="Cancel"
              severity="secondary"
              size="small"
              outlined
              onClick={() => {
                overlayPanelRef.current?.hide();
                setEditingTag(null);
              }}
              disabled={bulkUpsertMutation.isLoading}
              className="flex-1"
            />
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
};

// Export component without memo to ensure proper re-renders for dropdown initialization
export const GroupFieldSection = GroupFieldSectionComponent;
